// LocatesNotFoundPopup.js
import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectFunds } from "../../../../Redux/Reducers/fundsSlice";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../Redux/Reducers/uiSlice";
import Existing from "./Existing";
import Target from "./Target";
import {
  TABLE_HEAD_AFTERORDERS,
  TABLE_HEAD_EXISTING,
  TABLE_HEAD_ORDER,
  TABLE_HEAD_TARGET,
} from "../../../../component/Methods/columns";
import Order from "./Order";
import AfterOrder from "./AfterOrder";
import { sendWebSocketMessage } from "../../../../hooks/useWebSocket";
import { selectBrokers, selectAllTrades, selectValidatedData } from "../../../../Redux/Reducers/selectors";

const ValidatedData = ({ title, open, onClose }) => {
  const funds = useSelector(selectFunds);
  const AllTrades = useSelector(selectAllTrades);
  const validatedData = useSelector(selectValidatedData);
   
  const brokers = useSelector(selectBrokers);
  const [selectedFund, setSelectedFund] = useState(null);
  const [ticker, setTicker] = useState("");
  const [existingPositions, setExistingPositions] = useState({});
  const [targetPositions, setTargetPositions] = useState({});
  const [orders, setOrders] = useState({});
  const [afterOrders, setAfterOrders] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [validatedFund, setValidatedFund] = useState(null);
  const [validatedTicker, setValidatedTicker] = useState("");
  const dispatch = useDispatch();

  const isFormValid = selectedFund && ticker.trim() !== "";

  const isDataEmpty = 
    hasSubmitted &&
    Object.keys(existingPositions).length === 0 &&
    Object.keys(targetPositions).length === 0 &&
    Object.keys(orders).length === 0 &&
    Object.keys(afterOrders).length === 0;

  const sortedFunds = [...(funds || [])]?.sort((a, b) => 
    (a?.abbreviation || '').localeCompare(b?.abbreviation || '')
  );

  useEffect(() => {
    if (open) {
      setSelectedFund(null);
      setTicker("");
      setExistingPositions({});
      setTargetPositions({});
      setOrders({});
      setAfterOrders({});
      setHasSubmitted(false);
      setValidatedFund(null);
      setValidatedTicker("");
    }
  }, [open]);

  useEffect(() => {
    if (validatedData) {
      const { existingPositions, targetPositions, orders, afterOrders } =
        validatedData;
      setExistingPositions(existingPositions || {});
      setTargetPositions(targetPositions || {});
      setOrders(orders || {});
      setAfterOrders(afterOrders || {});
    }
  }, [validatedData]);

  const handleSubmit = () => {
    setHasSubmitted(true);
    setValidatedFund(selectedFund);
    setValidatedTicker(ticker);
    const socketRequest = {
      action: "rebalancer_validate",
      data: {
        fund: selectedFund?.fundId,
        processId: AllTrades?.processId,
        ticker: ticker.toLowerCase(),
      },
    };

    dispatch(updateLoadingMsg("Validating Data"));
    dispatch(addActiveRequests("rebalancer_validate"));
    dispatch(updateLoading(true));

    sendWebSocketMessage(JSON.stringify(socketRequest));
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      sx={{
        "& .css-1t1j96h-MuiPaper-root-MuiDialog-paper": {
          width: validatedData ? "70%" : "40%",
          height: validatedData ? "75%" : "auto",
          maxWidth: "none",
          margin: 0,
        },
      }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            align="left"
            style={{ flexGrow: 1, fontSize: "20px" }}
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          display: "flex", 
          gap: 2, 
          alignItems: "center", 
          mt: 2,
          width: "100%",
          justifyContent: "center"
        }}>
          <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
            <Autocomplete
              options={sortedFunds}
              getOptionLabel={(option) => option?.abbreviation || ""}
              value={selectedFund}
              onChange={(event, newValue) => setSelectedFund(newValue)}
              sx={{ width: "50%" }}
              renderInput={(params) => (
                <TextField {...params} label="Select Fund" required />
              )}
            />
            <TextField
              label="Ticker"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              sx={{ width: "30%" }}
              required
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ width: "100px" }}
              disabled={!isFormValid}
            >
              Validate
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}>
          {isDataEmpty ? (
            <Typography 
              sx={{ 
                textAlign: 'center', 
                color: 'text.secondary',
                mt: 4,
                fontSize: '16px'
              }}
            >
              No validation data found for {validatedFund?.abbreviation} - {validatedTicker.toUpperCase()}
            </Typography>
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 2 }}>
                {Object.keys(existingPositions).length > 0 && (
                  <Existing
                    existing={existingPositions}
                    tableHeader={TABLE_HEAD_EXISTING}
                  />
                )}
                {Object.keys(targetPositions).length > 0 && (
                  <Target target={targetPositions} tableHeader={TABLE_HEAD_TARGET} />
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                {Object.keys(orders).length > 0 && (
                  <Order orders={orders} tableHeader={TABLE_HEAD_ORDER} brokers={brokers} />
                )}
                {Object.keys(afterOrders).length > 0 && (
                  <AfterOrder
                    afterOrders={afterOrders}
                    tableHeader={TABLE_HEAD_AFTERORDERS}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ValidatedData;
